/**
 * cacheLocal — o último estado que a tela já mostrou, guardado no aparelho.
 *
 * [Bruno 2026-08-25] Abrir o app dispara sete chamadas e a tela nasce VAZIA até
 * a última voltar. Em 4G ruim isso é a diferença entre "instantâneo" e "será que
 * travou?". Aqui a home pinta o que viu por último e troca pelos números frescos
 * quando eles chegam.
 *
 * TRÊS REGRAS, e a primeira é fiscal:
 *
 *  1. DADO GUARDADO NUNCA SE PASSA POR FRESCO. Todo registro leva a data em que
 *     foi lido, e a tela mostra "sem conexão · dados de tal hora" quando serve
 *     do cache. Valor de guia em aberto muda todo dia (juros e multa) — número
 *     velho sem etiqueta é pior que tela vazia.
 *
 *  2. É POR EMPRESA. A chave carrega o cliente_id do token; quem tem duas
 *     empresas não pode ver o número de uma na tela da outra. Sem token, nada é
 *     lido nem gravado.
 *
 *  3. VENCE. Passou de VALIDADE_DIAS, some — não existe motivo pra mostrar a
 *     situação fiscal da semana passada como se fosse a de agora.
 *
 * Não guarda PDF, não guarda nada que a LGPD chame de sensível além do que a
 * própria tela já mostra, e `limparTudo()` roda no logout.
 */

const PREFIXO = 'sa_cache_';
const VALIDADE_DIAS = 7;

/** cliente_id do JWT, só pra separar as gavetas. Sem verificar assinatura — não é decisão de segurança. */
function _clienteId() {
  try {
    const t = localStorage.getItem('cliente_token');
    if (!t) return null;
    const corpo = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return corpo?.cliente_id || null;
  } catch (_) { return null; }
}

function _chave(nome) {
  const id = _clienteId();
  return id ? `${PREFIXO}${id}_${nome}` : null;
}

/**
 * Guarda o estado que a tela acabou de receber.
 * Silencioso em qualquer erro (cota cheia, modo privativo): cache é conforto,
 * nunca pode derrubar a tela.
 */
export function salvar(nome, dados) {
  const k = _chave(nome);
  if (!k) return;
  try {
    localStorage.setItem(k, JSON.stringify({ em: new Date().toISOString(), dados }));
  } catch (_) { /* sem espaço / modo privativo: segue a vida */ }
}

/**
 * Lê o último estado guardado.
 * @returns {{dados:any, em:string}|null} null quando não há, venceu ou está corrompido.
 */
export function ler(nome) {
  const k = _chave(nome);
  if (!k) return null;
  try {
    const cru = localStorage.getItem(k);
    if (!cru) return null;
    const obj = JSON.parse(cru);
    if (!obj || !obj.em) return null;
    const idade = Date.now() - new Date(obj.em).getTime();
    if (!Number.isFinite(idade) || idade > VALIDADE_DIAS * 86400000) {
      localStorage.removeItem(k);
      return null;
    }
    return { dados: obj.dados, em: obj.em };
  } catch (_) { return null; }
}

/** Apaga tudo o que este módulo guardou (logout, troca de conta). */
export function limparTudo() {
  try {
    const apagar = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIXO)) apagar.push(k);
    }
    apagar.forEach((k) => localStorage.removeItem(k));
  } catch (_) { /* nada a fazer */ }
}

/** "hoje às 14:32" / "24/08 às 14:32" — o carimbo que vai na tela. */
export function rotuloDe(em) {
  try {
    const d = new Date(em);
    if (Number.isNaN(d.getTime())) return null;
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const hoje = new Date();
    const mesmoDia = d.toDateString() === hoje.toDateString();
    if (mesmoDia) return `hoje às ${hora}`;
    return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${hora}`;
  } catch (_) { return null; }
}

const cacheLocal = { salvar, ler, limparTudo, rotuloDe };
export default cacheLocal;
